import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, Navigate, useLocation } from 'react-router-dom';

import ItemList from './components/ItemList';
import ProductDetail from './components/ProductDetail';
import AddProduct from './AddProduct';
import Login from './Login';
import CustomerSignup from './components/CustomerSignup';
import CustomerLogin from './components/CustomerLogin';
import SellerLogin from './components/SellerLogin';
import SellerDashboard from './components/SellerDashboard';

import Cart from "./components/Cart";
import Checkout from './components/Checkout';

import OrderHistory from './components/OrderHistory';
import AdminOrders from './components/AdminOrders';
import AdminProducts from './components/AdminProducts';
import EditItem from './components/EditItem';

import ContactUs from './pages/ContactUs';
import ShippingPolicy from './pages/ShippingPolicy';
import TermsConditions from './pages/TermsConditions';
import RefundPolicy from './pages/RefundPolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ForgotPassword from './pages/ForgotPassword';

import Footer from './components/Footer';
import bannerImage from './assets/header-banner.png';
import { getCartCount, readCustomer } from './utils/marketplace';
import { trackSupportClick } from './utils/analytics';
import './AppWrapper.css';

function AppWrapper() {
  const [userRole, setUserRole] = useState(null);
  const [cartCount, setCartCount] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const role = (() => {
      if (localStorage.getItem('admin-token')) return 'admin';
      if (localStorage.getItem('seller-token')) return 'seller';
      const customer = readCustomer();
      if (customer?.token) return 'customer';
      return null;
    })();
    setUserRole(role);
  }, [location]);

  useEffect(() => {
    setCartCount(getCartCount());
    const syncCart = () => setCartCount(getCartCount());
    window.addEventListener('cart-updated', syncCart);
    return () => window.removeEventListener('cart-updated', syncCart);
  }, [location]);

  // ADMIN login
  const handleAdminLogin = (token) => {
    localStorage.setItem('admin-token', token);
    localStorage.removeItem('customer'); // ensure we log out any customer
    localStorage.removeItem('seller');
    localStorage.removeItem('seller-token');
    setUserRole('admin');
    navigate('/add-product');
  };

  // CUSTOMER login — accepts unified object from CustomerLogin
  // { id, name, email, phone, token }
  const handleCustomerLogin = (unified) => {
    if (!unified || !unified.token) {
      navigate('/customer-login');
      return;
    }

    localStorage.setItem('customer', JSON.stringify(unified));

    // Remove any old keys (legacy)
    localStorage.removeItem('customer-token');
    localStorage.removeItem('customer-id');
    localStorage.removeItem('customer-name');
    localStorage.removeItem('admin-token');
    localStorage.removeItem('seller');
    localStorage.removeItem('seller-token');

    setUserRole('customer');

    const redirectTo = sessionStorage.getItem('redirectAfterLogin');
    sessionStorage.removeItem('redirectAfterLogin');

    if (redirectTo === 'whatsapp-support') {
      openWhatsAppChat();
    } else if (redirectTo === 'checkout') {
      navigate('/checkout');
    } else if (redirectTo === 'cart') {
      navigate('/cart');
    } else if (redirectTo) {
      navigate(redirectTo);
    } else {
      navigate('/');
    }
  };

  const handleSellerLogin = (seller) => {
    if (!seller?.token) {
      navigate('/seller/login');
      return;
    }
    localStorage.setItem('seller-token', seller.token);
    localStorage.setItem('seller', JSON.stringify(seller));
    localStorage.removeItem('admin-token');
    localStorage.removeItem('customer');
    setUserRole('seller');
    navigate('/seller/dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('admin-token');
    localStorage.removeItem('customer');
    localStorage.removeItem('seller');
    localStorage.removeItem('seller-token');
    localStorage.removeItem('customer-token');
    localStorage.removeItem('customer-id');
    localStorage.removeItem('customer-name');
    setUserRole(null);
    navigate('/');
  };

  const openWhatsAppChat = () => {
    const phoneNumber = '918072968764';
    const customer = readCustomer() || {};
    const name = customer.name || 'Customer';
    const id = customer.id || '';
    window.open(
      `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
        `Hello, I am ${name}. My Customer ID is ${id}. I need help with my order.`
      )}`,
      '_blank'
    );
  };

  const handleWhatsAppClick = () => {
    trackSupportClick({ channel: "whatsapp", context: "floating_support_button" });
    if (userRole !== 'customer') {
      sessionStorage.setItem('redirectAfterLogin', 'whatsapp-support');
      navigate('/customer-login');
    } else {
      openWhatsAppChat();
    }
  };

  return (
    <div className="app-wrapper">
      {isHomePage && (
        <div className="banner-container">
          <img src={bannerImage} alt="Vanigam Banner" className="banner-image" />
        </div>
      )}

      <nav className="navbar">
        <div className="nav-left">
          <Link to="/" className="brand-lockup">
            <span className="brand-title">AquaRoots Market</span>
            <span className="brand-subtitle">Live fish in Tamil Nadu. Accessories across India.</span>
          </Link>
        </div>

        <div className="nav-right">
          {userRole === 'customer' && (
            <>
              <Link to="/orders">My Orders</Link>

              {/* ✅ Cart link with count */}
              <Link to="/cart">
                Cart {cartCount > 0 ? `(${cartCount})` : ""}
              </Link>

              <Link to="/checkout">Checkout</Link>
              <button onClick={handleLogout}>Logout</button>
            </>
          )}

          {userRole === null && (
            <>
              <Link to="/customer-login">Customer Login</Link>
              <Link to="/customer-signup">Signup</Link>
            </>
          )}

          {userRole === 'admin' && (
            <>
              <Link to="/add-product">Add Product</Link>
              <Link to="/admin-orders">View Orders</Link>
              <Link to="/admin-products">Manage Products</Link>
              <button onClick={handleLogout}>Logout</button>
            </>
          )}

          {userRole === 'seller' && (
            <>
              <Link to="/seller/dashboard">Dashboard</Link>
              <Link to="/seller/dashboard#seller-products">Add Products</Link>
              <Link to="/seller/dashboard#seller-offers">Offers</Link>
              <Link to="/seller/dashboard#seller-listings">Listings</Link>
              <Link to="/">Storefront</Link>
              <button onClick={handleLogout}>Logout</button>
            </>
          )}
        </div>
      </nav>

      <div className="page-content">
        <Routes>
          <Route path="/" element={<ItemList />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/admin-login" element={<Login onLogin={handleAdminLogin} />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          <Route
            path="/customer-login"
            element={userRole === null ? <CustomerLogin onLogin={handleCustomerLogin} /> : <Navigate to="/" />}
          />
          <Route
            path="/customer-signup"
            element={userRole === null ? <CustomerSignup /> : <Navigate to="/" />}
          />
          <Route
            path="/seller/login"
            element={userRole === null ? <SellerLogin onLogin={handleSellerLogin} /> : <Navigate to="/" />}
          />
          <Route
            path="/seller/signup"
            element={<Navigate to="/seller/login" />}
          />

          <Route
            path="/cart"
            element={
              userRole === 'customer'
                ? <Cart />
                : (() => {
                    sessionStorage.setItem('redirectAfterLogin', 'cart');
                    return <Navigate to="/customer-login" />;
                  })()
            }
          />

          <Route
            path="/checkout"
            element={
              userRole === 'customer'
                ? <Checkout />
                : (() => {
                    sessionStorage.setItem('redirectAfterLogin', 'checkout');
                    return <Navigate to="/customer-login" />;
                  })()
            }
          />

          <Route
            path="/orders"
            element={userRole === 'customer' ? <OrderHistory /> : <Navigate to="/customer-login" />}
          />

          <Route
            path="/add-product"
            element={userRole === 'admin' ? <AddProduct /> : <Navigate to="/admin-login" />}
          />
          <Route
            path="/seller/dashboard"
            element={userRole === 'seller' ? <SellerDashboard /> : <Navigate to="/seller/login" />}
          />
          <Route
            path="/admin-orders"
            element={userRole === 'admin' ? <AdminOrders /> : <Navigate to="/admin-login" />}
          />
          <Route
            path="/admin-products"
            element={userRole === 'admin' ? <AdminProducts /> : <Navigate to="/admin-login" />}
          />
          <Route
            path="/edit-item/:id"
            element={userRole === 'admin' ? <EditItem /> : <Navigate to="/admin-login" />}
          />
        </Routes>
      </div>

      <button
        className="whatsapp-btn"
        onClick={handleWhatsAppClick}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#25D366',
          color: '#fff',
          padding: '10px 16px',
          borderRadius: '50%',
          fontSize: '22px',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.3)',
          zIndex: 9999
        }}
        title="Chat with us on WhatsApp"
      >
        💬
      </button>

      <Footer />
    </div>
  );
}

export default AppWrapper;
